import React, { useState, useEffect, createRef } from "react";

import "./passwordPin.scss";

const PasswordPin = ({ otp, setOtp, error, onEnter }) => {
  const [count] = useState(6);

  const [refs] = useState(
    Array(count)
      .fill(1)
      .map(() => createRef())
  );

  const updateOtpState = () => {
    const otpValue = refs.map((ref) => ref.current.value || "").join("");
    setOtp(otpValue);
  };

  const handleChange = (val, ref, idx) => {
    // Get only the last digit, remove non-numeric characters
    // This allows replacing existing digits when typing a new one
    const digit = val.replace(/\D/g, "").slice(-1);
    
    if (!digit) {
      ref.current.value = "";
      updateOtpState();
      return;
    }
    
    // Always replace the existing value with the new digit
    // This matches the behavior in sign up/sign in OTP containers
    ref.current.value = digit;
    
    // Update OTP state
    updateOtpState();
    
    // Move to next field if not the last one
    if (idx < count - 1) {
      refs[idx + 1].current.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Only process if pasted data contains digits
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.slice(0, count).split("");
      
      // Fill inputs starting from current index
      digits.forEach((digit, i) => {
        const targetIdx = idx + i;
        if (targetIdx < count && refs[targetIdx]?.current) {
          refs[targetIdx].current.value = digit;
        }
      });
      
      // Focus on the last filled input or the last input if all are filled
      const lastFilledIdx = Math.min(idx + digits.length - 1, count - 1);
      if (refs[lastFilledIdx]?.current) {
        refs[lastFilledIdx].current.focus();
      }
      
      updateOtpState();
    }
  };

  const isDigit = (k) => /^[0-9]$/.test(k);

  const handleKeyDown = (e, ref, idx) => {
    // Handle digit input - replace existing value when focused field has a digit
    if (isDigit(e.key)) {
      e.preventDefault();
      ref.current.value = e.key; // Directly replace with new digit
      updateOtpState();
      
      // Move to next field if not the last one
      if (idx < count - 1) {
        refs[idx + 1].current.focus();
      }
      return;
    }
    
    // Handle backspace
    if (e.key === "Backspace") {
      e.preventDefault();
      if (ref.current.value) {
        // If current field has value, clear it
        ref.current.value = "";
        updateOtpState();
      } else if (idx > 0) {
        // If current field is empty, move to previous and clear it
        refs[idx - 1].current.focus();
        refs[idx - 1].current.value = "";
        updateOtpState();
      }
    }
    
    // Handle arrow keys
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs[idx - 1].current.focus();
    }
    if (e.key === "ArrowRight" && idx < count - 1) {
      e.preventDefault();
      refs[idx + 1].current.focus();
    }
    
    // Handle delete key
    if (e.key === "Delete") {
      e.preventDefault();
      ref.current.value = "";
      updateOtpState();
    }
    
    // Handle Enter key - trigger verification if OTP is complete
    if (e.key === "Enter") {
      e.preventDefault();
      const currentOtp = refs.map((ref) => ref.current.value || "").join("");
      const isOtpComplete = currentOtp.length === count && currentOtp.split("").every((digit) => digit !== "");
      if (isOtpComplete && onEnter) {
        onEnter();
      }
    }
  };

  useEffect(() => {
    refs[0].current.focus();
  }, [refs]);

  return (
    <div className="verifyCodeBox">
      <div className="otpContainer">
        <div className="inputsWrapper">
          {refs.map((ref, idx) => (
            <input
              style={{ border: error ? "1px solid red " : "" }}
              ref={ref}
              maxLength={1}
              key={`input-${idx + 1}`}
              onChange={(e) => handleChange(e.target.value, ref, idx)}
              onKeyDown={(e) => handleKeyDown(e, ref, idx)}
              onPaste={(e) => handlePaste(e, idx)}
              inputMode="numeric"
              type="text"
              autoComplete="off"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PasswordPin;
